/** Deployment context is advisory; only ping determines availability. */
export interface DeploymentReason {
  label: string;
  text: string;
  expiresAt?: number;
}

export const UNKNOWN_DEPLOYMENT_REASON: DeploymentReason = {
  label: 'Reason',
  text: 'We don’t currently know why AIWB is unavailable.',
};

/** A browser clock running behind the publisher's must not make a fresh record look future-dated. */
export const DEPLOYMENT_CLOCK_SKEW_MS = 30_000;

/** Never display arbitrary publisher messages or infer an outage from a terminal record. */
export function deploymentReason(value: unknown, environment: string, now = Date.now()): DeploymentReason {
  if (!value || typeof value !== 'object') return UNKNOWN_DEPLOYMENT_REASON;
  const record = value as Record<string, unknown>;
  const updated = typeof record['updated_at'] === 'string' ? Date.parse(record['updated_at']) : NaN;
  const expires = typeof record['expires_at'] === 'string' ? Date.parse(record['expires_at']) : NaN;
  if (
    record['schema_version'] !== 1 ||
    record['environment'] !== environment ||
    record['service'] !== 'aiwb-api' ||
    record['reason_code'] !== 'deployment' ||
    typeof record['deployment_id'] !== 'string' ||
    !record['deployment_id'] ||
    !Number.isFinite(updated) ||
    !Number.isFinite(expires) ||
    updated > now + DEPLOYMENT_CLOCK_SKEW_MS ||
    expires <= now ||
    expires <= updated ||
    expires - updated > 95_000
  )
    return UNKNOWN_DEPLOYMENT_REASON;

  const version =
    typeof record['version'] === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/.test(record['version'])
      ? ` version ${record['version']}`
      : '';
  switch (record['stage']) {
    case 'deploying':
      return { label: 'Deployment status', text: `Installing API${version}.`, expiresAt: expires };
    case 'verifying':
      return { label: 'Deployment status', text: `Checking API${version} is ready.`, expiresAt: expires };
    case 'rolling_back':
      return { label: 'Deployment status', text: 'Restoring the previous API version.', expiresAt: expires };
    case 'failed':
      return { label: 'Reason', text: 'Unknown. The latest API deployment failed.', expiresAt: expires };
    default:
      return UNKNOWN_DEPLOYMENT_REASON;
  }
}

/** Native fetch bypasses the interceptor that holds API requests during an outage. */
export async function readDeploymentStatus(signal: AbortSignal): Promise<unknown> {
  try {
    const response = await fetch('/assets/status/aiwb-api.json', {
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return null;
    return await response.json();
  } catch {
    return null;
  }
}
